import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitCompetitorListRequest } from '../../../../store/features/actions/competitorListActions';
import { getCompanyCodesByName } from '../../../../services/accIqService';
import '../styles/FilterSidebar.css';
import NewsLetterDateRangePicker from './NewsLetterDateRangePicker';

const FilterSidebar = ({
    startDate = '',
    setStartDate,
    endDate = '',
    setEndDate,
    selectedNewsTypes = [],
    setSelectedNewsTypes,
    selectedRegions = [],
    setSelectedRegions,
    selectedCompanies = [],
    setSelectedCompanies,
    companyNamesMap = {}, // Map of company codes to names for instant lookups
    user,
    onCompaniesReady,
    onCompanyNameMapReady,
    onlyWithTimeline,
    setOnlyWithTimeline
}) => {
    const dispatch = useDispatch();
    const { loading: competitorLoading, data: competitorData } = useSelector(
        (state) => state.competitorListReducer
    );
    const { success: createSuccess } = useSelector(
        (state) => state.createCompetitorReducer
    );

    // Local state for date inputs (only update parent on Go click)
    const [localStartDate, setLocalStartDate] = useState(startDate || '');
    const [localEndDate, setLocalEndDate] = useState(endDate || '');

    // Sync with parent state changes
    useEffect(() => {
        setLocalStartDate(startDate || '');
        setLocalEndDate(endDate || '');
    }, [startDate, endDate]);

    // Local state for selected company names (for UI display)
    const [selectedCompanyNames, setSelectedCompanyNames] = useState([]);
    const selectedCompanyNamesRef = useRef(selectedCompanyNames);
    const [isLoadingCodes, setIsLoadingCodes] = useState(false);
    const hasInitialized = useRef(false);
    const lastNotifiedMapRef = useRef(null);

    // Build and maintain a persistent code-to-name mapping for instant lookup
    const [codeToNameMap, setCodeToNameMap] = useState({});
    const [nameToCodeMap, setNameToCodeMap] = useState({});

    // Fetch competitor list when user is available
    useEffect(() => {
        if (user?.company_name_for_growth_iq?.length > 0) {
            dispatch(submitCompetitorListRequest(user.company_name_for_growth_iq));
        }
    }, [user, dispatch, createSuccess]);

    // Extract company names from competitor list
    const companies = useMemo(() => {
        if (competitorData?.result?.length > 0) {
            // Deduplicate company names using Set
            const names = competitorData.result.map(c => c.company).filter(Boolean);
            const uniqueNames = [...new Set(names)];
            return ["All", ...uniqueNames];
        }
        return ["All"];
    }, [competitorData]);

    // Initialize company codes AND build the mapping via API (robust 1-by-1 fetch)
    useEffect(() => {
        const initializeCompanyCodes = async () => {
            // Only run once when competitors are loaded for the first time
            if (hasInitialized.current || competitorLoading || companies.length <= 1) {
                return;
            }

            hasInitialized.current = true;
            setIsLoadingCodes(true);

            try {
                // Get all company names (excluding "All")
                const allCompanyNames = companies.filter(c => c !== "All");

                if (allCompanyNames.length > 0) {
                    // Fetch codes INDIVIDUALLY to guarantee correct mapping order
                    // This solves the issue where bulk API calls return misordered results
                    const promises = allCompanyNames.map(async (name) => {
                        try {
                            const response = await getCompanyCodesByName([name]);
                            const code = response?.data?.data?.[0]; // Expecting single code
                            return { name, code };
                        } catch (err) {
                            console.error(`Failed to fetch code for ${name}`, err);
                            return { name, code: null };
                        }
                    });

                    const results = await Promise.all(promises);

                    // Build bidirectional mappings from the verified results
                    const codeToName = {};
                    const nameToCode = {};
                    const validCodes = [];

                    results.forEach(({ name, code }) => {
                        if (code) {
                            codeToName[code] = name;
                            nameToCode[name] = code;
                            validCodes.push(code);
                        }
                    });

                    setCodeToNameMap(codeToName);
                    setNameToCodeMap(nameToCode);

                    // Pass the mapping up to parent for display purposes
                    if (onCompanyNameMapReady) {
                        onCompanyNameMapReady(codeToName);
                    }

                    // Notify parent that company codes are ready
                    if (onCompaniesReady) {
                        onCompaniesReady(validCodes);
                    }
                } else {
                    // Empty state handling
                    setSelectedCompanies([]);
                    if (onCompaniesReady) {
                        onCompaniesReady([]);
                    }
                }
            } catch (error) {
                console.error('Error initializing company codes via API:', error);
                setSelectedCompanies([]);
                if (onCompaniesReady) {
                    onCompaniesReady([]);
                }
            } finally {
                setIsLoadingCodes(false);
            }
        };

        initializeCompanyCodes();
    }, [companies, competitorLoading, onCompaniesReady, onCompanyNameMapReady]);

    // Keep the ref in sync with state
    useEffect(() => {
        selectedCompanyNamesRef.current = selectedCompanyNames;
    }, [selectedCompanyNames]);

    // Sync selectedCompanies (codes) back to selectedCompanyNames when codes change externally
    // This handles when filters are added by clicking on cards
    useEffect(() => {
        // Skip during initial load
        if (!hasInitialized.current || competitorLoading) {
            return;
        }

        // If selectedCompanies is empty, clear selectedCompanyNames (unless we think it's "All")
        // But if selectedCompanies is empty, it usually means "All" in our new logic.
        if (selectedCompanies.length === 0) {
            setSelectedCompanyNames([]);
            return;
        }

        // Read current names from ref to avoid circular dependency
        const currentNames = selectedCompanyNamesRef.current;

        // Check if current selected names already match the selected codes
        // This prevents overwriting "Company A" with "Company B" if they share a code
        const currentMappedCodes = currentNames
            .map(name => nameToCodeMap[name])
            .filter(Boolean);

        const codesMatch =
            currentMappedCodes.length === selectedCompanies.length &&
            currentMappedCodes.every(code => selectedCompanies.includes(code)) &&
            selectedCompanies.every(code => currentMappedCodes.includes(code));

        if (codesMatch) {
            return;
        }

        // SMART MAPPING:
        // Instead of blind re-mapping, check if we already have a selected name that matches the code.
        // This prevents "Company A" (Code 123) from flipping to "Company B" (Code 123) just because Code 123 is selected.
        const names = selectedCompanies.map(code => {
            // 1. Check if any currently selected name maps to this code (PRESERVE SELECTION)
            const existingName = currentNames.find(n => nameToCodeMap[n] === code);
            if (existingName) return existingName;

            // 2. Fallback to parent map or internal map (lossy)
            return companyNamesMap[code] || codeToNameMap[code] || code;
        }).filter(Boolean);

        // Only update if the names have actually changed to avoid infinite loops
        if (JSON.stringify(names.sort()) !== JSON.stringify([...currentNames].sort())) {
            setSelectedCompanyNames(names);
        }
    }, [selectedCompanies, companyNamesMap, codeToNameMap, competitorLoading, nameToCodeMap]);

    // Lift state: Notify parent of code-to-name mapping for display based on CURRENT SELECTION
    // This ensures active filter bar shows "Company A" if selected, even if codeToNameMap thinks it's "Company B"
    useEffect(() => {
        if (onCompanyNameMapReady && selectedCompanyNames.length > 0) {
            const dynamicMap = {};
            selectedCompanyNames.forEach(name => {
                const code = nameToCodeMap[name];
                if (code) {
                    dynamicMap[code] = name;
                }
            });
            // Only notify parent if the map actually changed (prevents re-render loop)
            const mapKey = JSON.stringify(dynamicMap);
            if (mapKey !== lastNotifiedMapRef.current) {
                lastNotifiedMapRef.current = mapKey;
                onCompanyNameMapReady(dynamicMap);
            }
        }
    }, [selectedCompanyNames, nameToCodeMap, onCompanyNameMapReady]);


    // News types mapping: display name -> API value
    const newsTypesMap = {
        "Government and Regulatory News": "government_and_regulatory_news",
        "New Product & Project Updates": "new_product_and_project_updates",
        "Marketing Initiatives": "marketing_initiatives",
        "Other Company News": "other_company_news",
        "Financial Announcements": "financial_announcements",
        "Mergers & Acquisitions": "mergers_and_acquisitions",
        "Contract": "contract",
        "Investment & Capital Raising": "investment_and_capital_raising",
        "Research & Development": "research_and_development",
        "Exhibitions & Conferences": "exhibitions_and_conferences",
        "Joint Ventures Collaborations Partnerships": "joint_ventures_collaborations_partnerships"
    };

    // Regions mapping: display name -> API value
    const regionsMap = {
        "Asia-Pacific": "AP",
        "North America": "NM",
        "Europe": "EU",
        "Middle East & Africa": "MA",
        "South America": "SA"
    };

    const newsTypes = ["All", ...Object.keys(newsTypesMap)];
    const regions = ["All", ...Object.keys(regionsMap)];

    // Generic toggle filter for news types and regions
    const toggleFilter = (value, filterArray, setFilterArray) => {
        if (value === "All") {
            setFilterArray([]);
        } else {
            setFilterArray(prev =>
                prev.includes(value)
                    ? prev.filter(item => item !== value)
                    : [...prev, value]
            );
        }
    };

    // Handle company filter toggle
    const toggleCompanyFilter = useCallback((companyName) => {
        let newSelectedNames;

        if (companyName === "All") {
            // "All" selected - clear specific selections
            newSelectedNames = [];
        } else {
            // Toggle individual company
            newSelectedNames = selectedCompanyNames.includes(companyName)
                ? selectedCompanyNames.filter(name => name !== companyName)
                : [...selectedCompanyNames, companyName];
        }

        setSelectedCompanyNames(newSelectedNames);

        // Map names to codes using our reliable local map
        if (newSelectedNames.length === 0) {
            // "All" is selected -> Set empty list.
            // Parent component will interpret [] as "All"
            setSelectedCompanies([]);
        } else {
            // Specific companies selected
            const codes = newSelectedNames
                .map(name => nameToCodeMap[name])
                .filter(Boolean); // Filter out any missing codes (shouldn't happen)
            setSelectedCompanies(codes);
        }
    }, [selectedCompanyNames, nameToCodeMap, setSelectedCompanies]);

    return (
        <div className="filter-sidebar-news">
            <div className="filter-box-news">
                <h3 className="filter-title-news">Published Date</h3>
                <NewsLetterDateRangePicker
                  startDate={localEndDate}
                  endDate={localStartDate}
                  onDateChange={(start, end) => {
                    setLocalEndDate(start);
                    setLocalStartDate(end);
                    setEndDate(start);
                    setStartDate(end);
                  }}
                />
            </div>

            <div className="filter-box-news">
                <h3 className="filter-title-news">Companies</h3>
                <div className="checkbox-list-news">
                    {competitorLoading ? (
                        <span className="loading-text-news">Loading companies...</span>
                    ) : (
                        <>
                            {/* Render all companies from the original list */}
                            {companies.map(company => {
                                // Check if this company is selected:
                                // 1. By name (selectedCompanyNames) - for user interactions
                                // 2. By code (selectedCompanies via mapping) - for instant card clicks
                                const companyCode = nameToCodeMap[company];
                                const isChecked = company === "All"
                                    ? selectedCompanyNames.length === 0 && selectedCompanies.length === 0
                                    : selectedCompanyNames.includes(company);

                                return (
                                    <label key={company} className="checkbox-label-news">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleCompanyFilter(company)}
                                            className="checkbox-input-news"
                                            disabled={isLoadingCodes}
                                        />
                                        <span>{company}</span>
                                    </label>
                                );
                            })}

                            {/* Render selected companies clicked from cards that aren't in the original list */}
                            {selectedCompanies
                                .filter(code => {
                                    // Only show if not already in companies list
                                    const name = companyNamesMap[code] || codeToNameMap[code];
                                    return name && !companies.includes(name);
                                })
                                .map(code => {
                                    const displayName = companyNamesMap[code] || codeToNameMap[code] || code;
                                    return (
                                        <label key={code} className="checkbox-label-news">
                                            <input
                                                type="checkbox"
                                                checked={true}
                                                onChange={() => {
                                                    // Remove this company by code
                                                    setSelectedCompanies(prev => prev.filter(c => c !== code));
                                                }}
                                                className="checkbox-input-news"
                                            />
                                            <span>{displayName}</span>
                                        </label>
                                    );
                                })
                            }
                        </>
                    )}
                    {isLoadingCodes && (
                        <span className="loading-codes-news">Converting to codes...</span>
                    )}
                </div>
            </div>

            <div className="filter-box-news">
                <h3 className="filter-title-news">News Types</h3>
                <div className="checkbox-list-news">
                    {newsTypes.map(type => {
                        const apiValue = newsTypesMap[type];
                        return (
                            <label key={type} className="checkbox-label-news">
                                <input
                                    type="checkbox"
                                    checked={type === "All" ? selectedNewsTypes.length === 0 : selectedNewsTypes.includes(apiValue)}
                                    onChange={() => toggleFilter(type === "All" ? "All" : apiValue, selectedNewsTypes, setSelectedNewsTypes)}
                                    className="checkbox-input-news"
                                />
                                <span>{type}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="filter-box-news">
                <h3 className="filter-title-news">Regions</h3>
                <div className="checkbox-list-news">
                    {regions.map(region => {
                        const apiValue = regionsMap[region];
                        return (
                            <label key={region} className="checkbox-label-news">
                                <input
                                    type="checkbox"
                                    checked={region === "All" ? selectedRegions.length === 0 : selectedRegions.includes(apiValue)}
                                    onChange={() => toggleFilter(region === "All" ? "All" : apiValue, selectedRegions, setSelectedRegions)}
                                    className="checkbox-input-news"
                                />
                                <span>{region}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* ── Highlights ── */}
            {/* <div className="filter-box-news">
                <h3 className="filter-title-news">Highlights</h3>
                <div className="checkbox-list-news">
                    <label className="checkbox-label-news">
                        <input
                            type="checkbox"
                            checked={onlyWithTimeline}
                            onChange={() => setOnlyWithTimeline(!onlyWithTimeline)}
                            className="checkbox-input-news"
                        />
                        <span>News with Timelines</span>
                    </label>
                </div>
            </div> */}
        </div>
    );
};

export default FilterSidebar;